import { useState, type FormEvent } from 'react';
import { useSchedules, useSaveSchedule, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { ApiClientError } from '../../lib/api/client.js';
import type { Schedule } from '@arcnet/contracts';

/**
 * Business hours and holidays.
 *
 * Modelled as a WEEK plus a list of exceptions rather than a flat rule list,
 * because that is the shape of the thing being described. A flat list makes
 * "are we open on Tuesday?" a question you answer by reading every row and
 * working out which one wins.
 */

/**
 * Postgres returns a `time` as HH:MM:SS. An <input type="time"> with the
 * default step only accepts HH:MM and silently renders EMPTY otherwise, so a
 * saved schedule would open with blank times and quietly save 00:00.
 */
const hhmm = (t: string | null | undefined, fallback: string): string =>
  (t ?? fallback).slice(0, 5);

const DAYS = [
  { n: 1, label: 'Monday', short: 'Mon' },
  { n: 2, label: 'Tuesday', short: 'Tue' },
  { n: 3, label: 'Wednesday', short: 'Wed' },
  { n: 4, label: 'Thursday', short: 'Thu' },
  { n: 5, label: 'Friday', short: 'Fri' },
  { n: 6, label: 'Saturday', short: 'Sat' },
  { n: 0, label: 'Sunday', short: 'Sun' },
] as const;

/** Exceptions must beat the weekly pattern beneath them, hence the priority. */
const EXCEPTION_PRIORITY = 100;

interface DayDraft { open: boolean; from: string; to: string }
interface ExceptionDraft {
  id: string; label: string; from: string; to: string; isOpen: boolean;
}

const blankWeek = (): Record<number, DayDraft> => {
  const w: Record<number, DayDraft> = {};
  for (const d of DAYS) {
    // Weekdays 08:00-17:00 open, weekend closed: the shape of most South
    // African offices, and a starting point beats an empty grid.
    const weekday = d.n >= 1 && d.n <= 5;
    w[d.n] = { open: weekday, from: '08:00', to: '17:00' };
  }
  return w;
};

export function SchedulesTab({ tenantId }: { tenantId: string }) {
  const schedules = useSchedules(tenantId);
  const del = useDeleteTms(tenantId, 'schedules');
  const [editing, setEditing] = useState<Schedule | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Time schedules</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Business hours and holidays. Call flows route on whether a schedule is open.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New schedule'}
        </button>
      </div>

      {schedules.error && <ErrorBanner error={schedules.error} />}
      {del.error && <ErrorBanner error={del.error} />}

      {(creating || editing) && (
        <ScheduleEditor
          tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Open hours</th>
                <th className="arc-td-num">Exceptions</th><th>Right now</th><th />
              </tr>
            </thead>
            {schedules.isLoading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {schedules.data?.data.map((sc) => (
                  <tr key={sc.id}>
                    <td style={{ fontWeight: 550 }}>{sc.name}</td>
                    <td className="arc-muted">{summariseWeek(sc)}</td>
                    <td className="arc-td-num">
                      {sc.rules.filter((r) => r.kind !== 'weekly').length || (
                        <span className="arc-subtle">—</span>
                      )}
                    </td>
                    <td><OpenNow schedule={sc} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(sc); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete schedule '${sc.name}'?`)) del.mutate(sc.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!schedules.isLoading && schedules.data?.data.length === 0 && (
          <Empty icon="clock" title="No schedules"
            hint="A schedule lets a call flow send after-hours calls somewhere else." />
        )}
      </div>
    </>
  );
}

/**
 * Answers the only question anyone actually asks of a schedule.
 *
 * Evaluated in the BROWSER's clock, not the schedule's timezone, so it is
 * labelled as such rather than presented as authoritative. The switch decides
 * for real; this is a sanity check for the person editing.
 */
function OpenNow({ schedule }: { schedule: Schedule }) {
  const now = new Date();
  const now5 = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const today = now.toISOString().slice(0, 10);
  const dow = now.getDay();

  // Highest priority wins, which is what makes a holiday beat the week.
  const matched = [...schedule.rules]
    .sort((a, b) => b.priority - a.priority)
    .find((r) => {
      if (r.kind === 'weekly') {
        if (!r.days_of_week?.includes(dow)) return false;
        return !r.time_from || !r.time_to
          || (now5 >= hhmm(r.time_from, '00:00') && now5 < hhmm(r.time_to, '24:00'));
      }
      const from = r.date_from ?? '';
      const to = r.date_to ?? r.date_from ?? '';
      return today >= from && today <= to;
    });

  if (!matched) return <Badge tone="muted">closed</Badge>;
  return (
    <Badge tone={matched.is_open ? 'ok' : 'muted'}>
      <span className="arc-dot" aria-hidden="true" />
      {matched.is_open ? 'open' : 'closed'}
      {matched.label ? ` · ${matched.label}` : ''}
    </Badge>
  );
}

function summariseWeek(sc: Schedule): string {
  const weekly = sc.rules.filter((r) => r.kind === 'weekly' && r.is_open);
  if (weekly.length === 0) return 'never open';
  // Group days that share the same hours so the summary stays one line.
  const byHours = new Map<string, number[]>();
  for (const r of weekly) {
    const k = `${hhmm(r.time_from, '00:00')}–${hhmm(r.time_to, '24:00')}`;
    for (const d of r.days_of_week ?? []) byHours.set(k, [...(byHours.get(k) ?? []), d]);
  }
  return [...byHours.entries()]
    .map(([hours, days]) => {
      const names = DAYS.filter((d) => days.includes(d.n)).map((d) => d.short);
      return `${names.join(', ')} ${hours}`;
    })
    .join(' · ');
}

function ScheduleEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: Schedule | null; onDone: () => void;
}) {
  const save = useSaveSchedule(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [timezone, setTimezone] = useState(existing?.timezone ?? 'Africa/Johannesburg');

  const [week, setWeek] = useState<Record<number, DayDraft>>(() => {
    if (!existing) return blankWeek();
    const w: Record<number, DayDraft> = {};
    for (const d of DAYS) w[d.n] = { open: false, from: '08:00', to: '17:00' };
    for (const r of existing.rules) {
      if (r.kind !== 'weekly' || !r.is_open) continue;
      for (const d of r.days_of_week ?? []) {
        w[d] = { open: true, from: hhmm(r.time_from, '08:00'), to: hhmm(r.time_to, '17:00') };
      }
    }
    return w;
  });

  const [exceptions, setExceptions] = useState<ExceptionDraft[]>(() =>
    (existing?.rules ?? [])
      .filter((r) => r.kind !== 'weekly')
      .map((r) => ({
        id: r.id,
        label: r.label ?? '',
        from: r.date_from ?? '',
        to: r.date_to ?? r.date_from ?? '',
        isOpen: r.is_open,
      })),
  );

  const setDay = (n: number, patch: Partial<DayDraft>) =>
    setWeek((p) => ({ ...p, [n]: { ...p[n]!, ...patch } }));

  /** Copies Monday's hours down. The alternative is typing 09:00 five times. */
  const copyMondayDown = () => {
    const mon = week[1]!;
    setWeek((p) => {
      const next = { ...p };
      for (const d of DAYS) if (d.n >= 2 && d.n <= 5) next[d.n] = { ...mon };
      return next;
    });
  };

  const err = save.error instanceof ApiClientError ? save.error : null;
  const invalidDay = DAYS.find((d) => week[d.n]!.open && week[d.n]!.from >= week[d.n]!.to);

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name,
          timezone: timezone || null,
          rules: [
            ...DAYS.filter((d) => week[d.n]!.open).map((d) => ({
              kind: 'weekly' as const,
              days_of_week: [d.n],
              time_from: week[d.n]!.from,
              time_to: week[d.n]!.to,
              is_open: true,
              priority: 0,
            })),
            ...exceptions
              .filter((x) => x.from)
              .map((x) => ({
                kind: (x.to && x.to !== x.from ? 'date_range' : 'date') as 'date_range' | 'date',
                date_from: x.from,
                ...(x.to && x.to !== x.from ? { date_to: x.to } : {}),
                is_open: x.isOpen,
                priority: EXCEPTION_PRIORITY,
                ...(x.label ? { label: x.label } : {}),
              })),
          ],
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New schedule'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="sc-name">Name</label>
          <input id="sc-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Office hours" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="sc-tz">Timezone</label>
          <input id="sc-tz" className="arc-input" value={timezone}
            onChange={(e) => setTimezone(e.target.value)} placeholder="Africa/Johannesburg" />
          <p className="arc-hint">Hours are interpreted here, not in the caller's timezone.</p>
        </div>
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <label className="arc-label" style={{ margin: 0 }}>Open hours</label>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm" onClick={copyMondayDown}>
            Copy Monday to Tue–Fri
          </button>
        </div>

        <div className="arc-week">
          {DAYS.map((d) => {
            const day = week[d.n]!;
            const bad = day.open && day.from >= day.to;
            return (
              <div key={d.n} className="arc-week-row" data-open={day.open}>
                <div className="arc-week-day">
                  <Switch
                    checked={day.open} label={`${d.label} open`}
                    onChange={(open) => setDay(d.n, { open })}
                  />
                  <span>{d.label}</span>
                </div>
                {day.open ? (
                  <div className="arc-row" style={{ gap: 6 }}>
                    <input type="time" className="arc-input arc-time" value={day.from}
                      aria-label={`${d.label} opens`}
                      aria-invalid={bad}
                      onChange={(e) => setDay(d.n, { from: e.target.value })} />
                    <span className="arc-subtle">to</span>
                    <input type="time" className="arc-input arc-time" value={day.to}
                      aria-label={`${d.label} closes`}
                      aria-invalid={bad}
                      onChange={(e) => setDay(d.n, { to: e.target.value })} />
                  </div>
                ) : (
                  <span className="arc-subtle" style={{ fontSize: 12 }}>closed</span>
                )}
              </div>
            );
          })}
        </div>

        {invalidDay && (
          <div className="arc-banner arc-banner-warn" style={{ marginTop: 9 }}>
            <Icon name="warning" size={14} />
            <span>
              {invalidDay.label} closes before it opens. Overnight hours need two
              schedules — one for each side of midnight.
            </span>
          </div>
        )}
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 7 }}>
          <label className="arc-label" style={{ margin: 0 }}>Holidays and exceptions</label>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-sm"
            onClick={() => setExceptions((p) => [
              ...p, { id: crypto.randomUUID(), label: '', from: '', to: '', isOpen: false },
            ])}>
            <Icon name="plus" size={12} /> Add date
          </button>
        </div>

        {exceptions.length === 0 ? (
          <p className="arc-muted" style={{ fontSize: 12.5 }}>
            None. Public holidays follow the weekly pattern unless listed here.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {exceptions.map((x, i) => (
              <div key={x.id} className="arc-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <input className="arc-input" style={{ maxWidth: 200 }} placeholder="Christmas Day"
                  value={x.label} aria-label={`Exception ${i + 1} label`}
                  onChange={(e) => setExceptions((p) =>
                    p.map((y) => y.id === x.id ? { ...y, label: e.target.value } : y))} />
                <input type="date" className="arc-input" style={{ maxWidth: 150 }}
                  value={x.from} aria-label={`Exception ${i + 1} from`}
                  onChange={(e) => setExceptions((p) =>
                    p.map((y) => y.id === x.id ? { ...y, from: e.target.value } : y))} />
                <span className="arc-subtle">to</span>
                <input type="date" className="arc-input" style={{ maxWidth: 150 }}
                  value={x.to} aria-label={`Exception ${i + 1} to`}
                  onChange={(e) => setExceptions((p) =>
                    p.map((y) => y.id === x.id ? { ...y, to: e.target.value } : y))} />
                <select className="arc-input" style={{ maxWidth: 120 }}
                  value={x.isOpen ? 'open' : 'closed'} aria-label={`Exception ${i + 1} state`}
                  onChange={(e) => setExceptions((p) =>
                    p.map((y) => y.id === x.id ? { ...y, isOpen: e.target.value === 'open' } : y))}>
                  <option value="closed">Closed</option>
                  <option value="open">Open</option>
                </select>
                <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
                  aria-label={`Remove exception ${i + 1}`}
                  onClick={() => setExceptions((p) => p.filter((y) => y.id !== x.id))}>
                  <Icon name="trash" size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <button type="submit" className="arc-btn arc-btn-primary"
        disabled={save.isPending || invalidDay !== undefined}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create schedule'}
      </button>
    </form>
  );
}
